import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import apiService from "../../../../service/Api";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../../components/common/ShowToast";

interface ChildRoom {
  id: number;
  name: string;
}

interface AddFloorPlanModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  parentVenue: { id: number; name: string };
  childRooms: ChildRoom[];
}

const AddFloorPlanModal: React.FC<AddFloorPlanModalProps> = ({
  open,
  onClose,
  onSuccess,
  parentVenue,
  childRooms,
}) => {
  const isImageFile = (file: File) =>
    file.type.startsWith("image/") || /\.(png|jpe?g|webp)$/i.test(file.name);
  const [selectedVenueId, setSelectedVenueId] = useState<number>(
    parentVenue.id,
  );
  const [files, setFiles] = useState<File[]>([]);
  const [floorPlanType, setFloorPlanType] = useState<
    "floor_plan" | "mobile_floor_plan"
  >("floor_plan");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const handleClose = () => {
    setFiles([]);
    setSelectedVenueId(parentVenue.id);
    setFloorPlanType("floor_plan");
    setUploadProgress({ done: 0, total: 0 });
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []).filter((file) => {
      if (floorPlanType !== "mobile_floor_plan") return true;
      return isImageFile(file);
    });
    if (
      floorPlanType === "mobile_floor_plan" &&
      picked.length !== (e.target.files?.length ?? 0)
    ) {
      showErrorToast("Mobile floor plans must be image files.");
    }
    if (picked.length) {
      setFiles((prev) => {
        const existingKeys = new Set(prev.map((f) => `${f.name}_${f.size}`));
        const newOnes = picked.filter(
          (f) => !existingKeys.has(`${f.name}_${f.size}`),
        );
        return [...prev, ...newOnes];
      });
    }
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!files.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });

    let successCount = 0;
    let failCount = 0;

    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("file_type", floorPlanType);
        fd.append("venue", String(selectedVenueId));
        await apiService.post("/api/venues/document/", fd, false);
        successCount += 1;
      } catch {
        failCount += 1;
      } finally {
        setUploadProgress((prev) => ({ ...prev, done: prev.done + 1 }));
      }
    }

    setUploading(false);

    if (successCount && !failCount) {
      showSuccessToast(
        successCount === 1
          ? "Floor plan uploaded successfully."
          : `${successCount} floor plans uploaded successfully.`,
      );
    } else if (successCount && failCount) {
      showErrorToast(
        `${successCount} uploaded, ${failCount} failed. Please retry the failed ones.`,
      );
    } else {
      showErrorToast("Failed to upload floor plans.");
    }

    handleClose();
    if (successCount) onSuccess();
  };

  const venueOptions = [
    { id: parentVenue.id, label: `${parentVenue.name} (Main Venue)` },
    ...childRooms.map((r) => ({ id: r.id, label: r.name })),
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, p: 1 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          fontSize: "1.1rem",
          pb: 1,
        }}
      >
        Add Floor Plans
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
      >
        {/* Modern Venue / Room Selector */}
        <FormControl fullWidth size="medium" sx={{ mt: 1 }}>
          <InputLabel id="upload-to-label">Upload destination room</InputLabel>
          <Select
            labelId="upload-to-label"
            value={selectedVenueId}
            label="Upload destination room"
            onChange={(e) => setSelectedVenueId(Number(e.target.value))}
            sx={{
              borderRadius: 1.5,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0,0,0,0.15)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#C29363",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#C29363",
              },
            }}
          >
            {venueOptions.map((o) => (
              <MenuItem key={o.id} value={o.id} sx={{ py: 1.2 }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="medium">
          <InputLabel id="floor-plan-type-label">Platform</InputLabel>
          <Select
            labelId="floor-plan-type-label"
            value={floorPlanType}
            label="Platform"
            onChange={(e) => {
              const nextType = e.target.value as
                | "floor_plan"
                | "mobile_floor_plan";
              setFloorPlanType(nextType);
              if (nextType === "mobile_floor_plan") {
                setFiles((current) =>
                  current.filter(isImageFile),
                );
              }
            }}
            sx={{ borderRadius: 1.5 }}
          >
            <MenuItem value="floor_plan">Web</MenuItem>
            <MenuItem value="mobile_floor_plan">Mobile</MenuItem>
          </Select>
        </FormControl>

        {/* Enhanced Dashed Drag & Click Upload Area */}
        <Box>
          <input
            id="floor-plan-file-input"
            type="file"
            accept={
              floorPlanType === "mobile_floor_plan"
                ? "image/*"
                : "image/*,application/pdf"
            }
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <label
            htmlFor="floor-plan-file-input"
            style={{ width: "100%", display: "block" }}
          >
            <Box
              sx={{
                width: "100%",
                minHeight: files.length ? "auto" : 160,
                border: "2px dashed",
                borderColor: files.length ? "#C29363" : "rgba(0, 0, 0, 0.15)",
                borderRadius: 2,
                backgroundColor: files.length
                  ? "rgba(194, 147, 99, 0.03)"
                  : "grey.50",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "#C29363",
                  backgroundColor: "rgba(194, 147, 99, 0.06)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {files.length === 0 ? (
                <>
                  <CloudUploadIcon
                    sx={{ fontSize: 44, color: "text.secondary", mb: 1.5 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 0.5, color: "text.primary" }}
                  >
                    Click to browse files
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF, PNG, JPG or JPEG · select multiple
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 0.5, color: "text.primary" }}
                  >
                    {files.length} file{files.length > 1 ? "s" : ""} selected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to add more
                  </Typography>
                </>
              )}
            </Box>
          </label>

          {/* Selected files list */}
          {files.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                mt: 1.5,
                maxHeight: 180,
                overflowY: "auto",
              }}
            >
              {files.map((f, index) => {
                const isPdf =
                  f.type === "application/pdf" ||
                  f.name.toLowerCase().endsWith(".pdf");
                return (
                  <Box
                    key={`${f.name}_${f.size}_${index}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: "1px solid rgba(0,0,0,0.1)",
                      bgcolor: "background.paper",
                    }}
                  >
                    {isPdf ? (
                      <PdfIcon sx={{ fontSize: 18, color: "#C9302C" }} />
                    ) : f.type.startsWith("image/") ? (
                      <Box
                        component="img"
                        src={URL.createObjectURL(f)}
                        alt={f.name}
                        sx={{
                          width: 22,
                          height: 22,
                          objectFit: "cover",
                          borderRadius: 0.5,
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <FileIcon sx={{ fontSize: 18, color: "#C29363" }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {f.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      sx={{ p: "3px" }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Button
          onClick={handleClose}
          size="medium"
          sx={{
            textTransform: "none",
            color: "text.secondary",
            fontWeight: 500,
            borderRadius: 1.5,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!files.length || uploading}
          variant="contained"
          size="medium"
          sx={{
            textTransform: "none",
            bgcolor: "#C29363",
            fontWeight: 600,
            px: 3,
            borderRadius: 1.5,
            boxShadow: "none",
            "&:hover": { bgcolor: "#a87d4f", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "rgba(0, 0, 0, 0.12)" },
          }}
          startIcon={
            uploading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {uploading
            ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
            : files.length > 1
              ? `Upload ${files.length} Plans`
              : "Upload Plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFloorPlanModal;
